import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { trackById } from '@core/utils/utils';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { Job } from '@models/job/job';
import { Link } from '@models/link/link';
import { LinkTypeEnum } from '@models/link/link-type.enum';
import { Bookmark } from '@models/profile/bookmark';
import { JobSearchContextEnum } from '@models/search/job-search-context.enum';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { CompanyRoutingFragmentEnum } from '@routing/company/company-routing.enum';
import { JobRoutingEnum } from '@routing/job/job-routing.enum';
import { CompanyState } from '@store/company/company.state';
import { BookmarkToggle } from '@store/profile/bookmark/bookmark.actions';
import { BookmarkState } from '@store/profile/bookmark/bookmark.state';
import { JobSearchState } from '@store/search/job-search/job-search.state';
import { buildPath, Params } from '@wizbii/angular-utilities';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';

@Component({
  selector: 'company-other-info',
  templateUrl: './other-info.component.html',
  styleUrls: ['./other-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtherInfoComponent {
  @Select(JobSearchState.result(JobSearchContextEnum.Company, 'hits')) jobs$: Observable<Job[]>;
  @Select(JobSearchState.result(JobSearchContextEnum.Company, 'nbHits')) total$: Observable<number>;
  @Select(BookmarkState) bookmarkMap$: Observable<Map<string, Bookmark>>;

  links$: Observable<Link[]>;
  twitterLink$: Observable<string>;
  companyJobsPath$: Observable<string[]>;
  jobSearchQueryParams$: Observable<Params>;

  CompanyRoutingFragmentEnum = CompanyRoutingFragmentEnum;
  JobRoutingEnum = JobRoutingEnum;
  trackById = trackById;

  constructor(private readonly store: Store, route: ActivatedRoute) {
    this.links$ = route.paramMap.pipe(
      map((paramMap) => paramMap.get('companyId')),
      switchMap((companyId) => this.store.select(CompanyState.company(companyId))),
      map((company) => company.links),
      map((links) => links.filter((link) => Object.values(LinkTypeEnum).includes(link.type))),
      filter((links) => links.length > 0),
      shareReplay()
    );

    this.twitterLink$ = this.links$.pipe(
      map((links) => links.find((link) => link.type === LinkTypeEnum.Twitter)),
      filter((link) => !!link),
      map((link) => `${/^http/.test(link.url) ? '' : 'https://twitter.com/'}${link.url}?ref_src=twsrc%5Etfw`)
    );

    this.companyJobsPath$ = route.paramMap.pipe(
      map((paramMap) => paramMap.get('companyId')),
      map((companyId) => [buildPath(FeaturesRoutingEnum.Company, companyId)]),
      shareReplay()
    );

    this.jobSearchQueryParams$ = route.paramMap.pipe(
      map((paramMap) => paramMap.get('companyId')),
      map((companyId) => ({ [JobSearchContextEnum.Company]: companyId })),
      shareReplay()
    );
  }

  @Dispatch() toggleBookmark = (job: Job): BookmarkToggle => new BookmarkToggle(job.id);

  trackByLink(_: any, item: Link): string {
    return item.type;
  }
}
